/**
 * Shift a date string by a number of days
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {number} deltaDays - Number of days to shift (positive or negative)
 * @returns {string} - Shifted date in YYYY-MM-DD format
 */
function shiftDate(dateStr, deltaDays) {
    if (!dateStr) return dateStr;
    const date = new Date(dateStr);
    date.setDate(date.getDate() + deltaDays);
    return date.toISOString().split('T')[0];
}

/**
 * Apply a task update operation to subtasks array
 */
function applyTaskUpdate(subtasks, payload) {
    return subtasks.map(st =>
        st.id === payload.taskId
            ? {
                ...st,
                start: payload.start,
                due_date: payload.end,
                custom_attributes_values: {
                    ...st.custom_attributes_values,
                    'Start Date': payload.start
                }
            }
            : st
    );
}

/**
 * Apply a parent move operation to subtasks array
 */
function applyParentMove(subtasks, payload) {
    const { affectedSubtaskIds, deltaDays } = payload;
    return subtasks.map(st => {
        if (affectedSubtaskIds.includes(st.id)) {
            const currentStart = st.start || st.custom_attributes_values?.['Start Date'] || st.due_date;
            const newStart = shiftDate(currentStart, deltaDays);
            const newEnd = shiftDate(st.due_date, deltaDays);
            return {
                ...st,
                start: newStart,
                due_date: newEnd,
                custom_attributes_values: {
                    ...st.custom_attributes_values,
                    'Start Date': newStart
                }
            };
        }
        return st;
    });
}

/**
 * Apply a status change operation to subtasks array
 */
function applyStatusChange(subtasks, payload) {
    return subtasks.map(st =>
        st.id === payload.taskId
            ? { ...st, status: payload.statusId }
            : st
    );
}

/**
 * Apply a weekend work toggle operation
 */
function applyWeekendWork(weekendWork, payload) {
    const { subtaskId, dateKey, worked } = payload;
    const current = { ...(weekendWork[subtaskId] || {}) };

    if (worked) {
        current[dateKey] = true;
    } else {
        delete current[dateKey];
    }

    return { ...weekendWork, [subtaskId]: current };
}

/**
 * Apply all pending operations to server state to get optimistic state
 * @param {Object} serverState - Current state from server
 * @param {Array} pendingOperations - Queue of pending operations
 * @returns {Object} - Merged optimistic state
 */
export function applyPendingOperations(serverState, pendingOperations) {
    if (!pendingOperations || pendingOperations.length === 0) {
        return serverState;
    }

    let result = {
        subtasks: [...(serverState.subtasks || [])],
        links: [...(serverState.links || [])],
        weekendWork: { ...(serverState.weekendWork || {}) },
        kickOffDate: serverState.kickOffDate
    };

    for (const op of pendingOperations) {
        switch (op.type) {
            case 'TASK_UPDATE':
                result.subtasks = applyTaskUpdate(result.subtasks, op.payload);
                break;

            case 'PARENT_MOVE':
                result.subtasks = applyParentMove(result.subtasks, op.payload);
                break;

            case 'DEPENDENCY_CREATE':
                result.links = [...result.links, op.payload.link];
                break;

            case 'DEPENDENCY_DELETE':
                result.links = result.links.filter(l =>
                    !(String(l.source) === String(op.payload.sourceId) && String(l.target) === String(op.payload.targetId))
                );
                break;

            case 'DEPENDENCY_COLOR_CHANGE':
                result.links = result.links.map(l =>
                    (String(l.source) === String(op.payload.sourceId) && String(l.target) === String(op.payload.targetId))
                        ? { ...l, color: op.payload.color }
                        : l
                );
                break;

            case 'STATUS_CHANGE':
                result.subtasks = applyStatusChange(result.subtasks, op.payload);
                break;

            case 'SUBTASK_ORDER':
                // Subtask order now uses task.order field, updated via API
                // No optimistic UI for this operation
                break;

            case 'WEEKEND_WORK':
                result.weekendWork = applyWeekendWork(result.weekendWork, op.payload);
                break;

            case 'KICKOFF_DATE':
                result.kickOffDate = op.payload.date;
                break;

            default:
                console.warn('Unknown operation type:', op.type);
        }
    }

    return result;
}
